import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';


const DOC_TITLES = {
  // Canonical platform TOS in DB: platform_terms. Legacy key terms_of_service kept for any old test paths.
  platform_terms: 'Terminos de la plataforma',
  terms_of_service: 'Terminos y Condiciones del Servicio',
  privacy_policy: 'Politica de Privacidad',
  artist_terms: 'Terminos para Artistas',
  payout_terms: 'Terminos de Liquidacion',
  ai_usage_policy: 'Politica de Uso de IA Musical',
  booking_inquire_terms: 'Consulta de contratacion (fase informativa)',
  booking_terms: 'Terminos de Contratacion (reserva formal)',
  community_rules: 'Normas de la Comunidad',
  refund_policy: 'Politica de Reembolsos',
  cookies_policy: 'Politica de Cookies',
  cookie_policy: 'Politica de Cookies',
  ip_takedown_policy: 'Politica de Propiedad Intelectual',
};

export default function LegalAcceptanceModal({ documentSlug, onAccepted, onClose, required = true }) {
  const { token } = useAuth();
  const { lang: appLang } = useLang();
  const l = appLang === 'en' ? 'en' : 'es';
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const fetchDoc = useCallback(async () => {
    try {
      // Published, in-force text (approved in DB) — same surface as unauthenticated browse
      const publicUrl =
        documentSlug === 'platform_terms'
          ? `${API}/api/legal/terms?lang=${l}`
          : documentSlug === 'privacy_policy'
            ? `${API}/api/legal/privacy?lang=${l}`
            : documentSlug === 'cookie_policy' || documentSlug === 'cookies_policy'
              ? `${API}/api/legal/cookie-policy?lang=${l}`
              : documentSlug === 'copyright_content_policy'
                ? `${API}/api/legal/copyright-policy?lang=${l}`
                : `${API}/api/legal/documents/${encodeURIComponent(documentSlug)}?lang=${l}`;
      const res = await fetch(publicUrl, {
        headers: documentSlug === 'platform_terms' || documentSlug === 'privacy_policy' || documentSlug === 'cookie_policy' || documentSlug === 'cookies_policy' || documentSlug === 'copyright_content_policy'
          ? {}
          : { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDoc(data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [documentSlug, token, l]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50) {
      setScrolledToEnd(true);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`${API}/api/legal/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug: documentSlug }),
      });
      if (res.ok) {
        if (onAccepted) onAccepted(documentSlug);
      }
    } catch (e) { console.error(e); }
    setAccepting(false);
  };

  const title = DOC_TITLES[documentSlug] || doc?.title || documentSlug;
  const sections = doc?.sections && doc.sections.length > 0
    ? doc.sections
    : doc?.content
      ? [{ id: 'body', heading: l === 'en' ? 'Full text' : 'Texto', content: doc.content }]
      : [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid={`legal-modal-${documentSlug}`}>
      <div className="bg-slate-800 rounded-2xl max-w-lg w-full border border-slate-600/40 overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-700/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
              <Shield size={20} className="text-indigo-400" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">{title}</h3>
              <p className="text-[10px] text-slate-500">
                {doc?.version ? `Version ${doc.version}` : 'Cargando...'}
                {required && ' — Aceptacion obligatoria'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4" onScroll={handleScroll} data-testid="legal-doc-content">
          {loading ? (
            <div className="text-center py-8 text-slate-500">
              {l === 'en' ? 'Loading document…' : 'Cargando documento...'}
            </div>
          ) : sections.length > 0 ? (
            <div className="space-y-4">
              {(expanded ? sections : sections.slice(0, 3)).map((section, idx) => (
                <div key={section.id || idx} className="bg-slate-900/40 rounded-lg p-3">
                  <h4 className="text-xs font-bold text-slate-300 mb-1.5">{section.heading}</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{section.content}</p>
                </div>
              ))}
              {!expanded && sections.length > 3 && (
                <button onClick={() => setExpanded(true)} className="w-full flex items-center justify-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 py-2 transition" data-testid="expand-legal-doc">
                  <ChevronDown size={14} /> Ver documento completo ({sections.length - 3} secciones mas)
                </button>
              )}
              {expanded && (
                <button onClick={() => setExpanded(false)} className="w-full flex items-center justify-center gap-1 text-xs text-slate-500 py-2">
                  <ChevronUp size={14} /> Contraer
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-red-400 text-sm">Error al cargar el documento</div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-slate-700/50 shrink-0 space-y-3">
          <p className="text-[10px] text-slate-500 text-center">
            Al aceptar, confirmas que has leido y comprendes este documento.
          </p>
          <div className="flex gap-2">
            {!required && onClose && (
              <button onClick={onClose} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-xl text-sm transition" data-testid="legal-decline-btn">
                Ahora no
              </button>
            )}
            <button
              onClick={handleAccept}
              disabled={accepting || loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50"
              data-testid="legal-accept-btn"
            >
              {accepting ? 'Aceptando...' : <><Check size={14} /> Acepto</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * useLegalGate — Hook that gates an action behind legal requirements.
 * Usage:
 *   const legal = useLegalGate('generate_ai');
 *   // In your action handler:
 *   if (!legal.allowed) return; // modal will show automatically
 *   // proceed with action...
 *
 * Returns: { allowed, checking, legalModal }
 *   - allowed: boolean - whether the user can proceed
 *   - checking: boolean - whether the check is in progress
 *   - legalModal: JSX element to render (or null)
 *   - recheck: function to re-check after acceptance
 */
export function useLegalGate(action) {
  const { token } = useAuth();
  const [allowed, setAllowed] = useState(null);
  const [missing, setMissing] = useState([]);
  const [showDoc, setShowDoc] = useState(null);

  const check = useCallback(async () => {
    if (!token || !action) return;
    try {
      const res = await fetch(`${API}/api/legal/check/${action}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllowed(data.allowed);
        setMissing(data.missing || []);
      }
    } catch (e) { console.error(e); }
  }, [token, action]);

  useEffect(() => { check(); }, [check]);

  const gate = useCallback(() => {
    if (allowed) return true;
    if (missing.length > 0) {
      setShowDoc(missing[0]);
    }
    return false;
  }, [allowed, missing]);

  const handleAccepted = useCallback(() => {
    setShowDoc(null);
    check();
  }, [check]);

  const legalModal = showDoc ? (
    <LegalAcceptanceModal
      documentSlug={showDoc}
      onAccepted={handleAccepted}
      required={true}
    />
  ) : null;

  return { allowed, checking: allowed === null, missing, gate, legalModal, recheck: check };
}

/**
 * useLegalCheck — Simple hook to check legal status without gating.
 */
export function useLegalCheck(action) {
  const { token } = useAuth();
  const [result, setResult] = useState(null);

  const check = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/legal/check/${action}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setResult(await res.json());
    } catch (e) { console.error(e); }
  }, [token, action]);

  useEffect(() => { check(); }, [check]);

  return { ...result, recheck: check };
}
